import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const CARDS_PER_GROUP = 5;

// Single fetch function that handles both steps
const fetchLaunchedCards = async (categoryId) => {
  try {
    // Primero obtenemos todas las cards lanzadas
    const { data: launchedCards, error: launchedError } = await supabase
      .from("cards")
      .select("id")
      .eq("launched", true)
      .order("companyname", { ascending: true });

    if (launchedError) throw launchedError;
    
    if (!launchedCards?.length) {
      return [];
    }

    const launchedIds = launchedCards.map(card => card.id);

    // Luego obtenemos los detalles completos aplicando los filtros necesarios
    let query = supabase
      .from("cards")
      .select(`
        id,
        companyname
      `)
      .in('id', launchedIds)
      .order('companyname', { ascending: true });

    // Aplicar filtro de categoría si no es "All"
    if (categoryId !== 9) {
      query = query.eq("category_id", categoryId);
    }

    const { data: categoryCards, error: categoryError } = await query;
    if (categoryError) throw categoryError;

    return categoryCards;
  } catch (error) {
    throw error;
  }
};

export function useLaunchedSubCategories({
  componentSize = "Desktop 1440",
  selectedCategoryId = 9
}) {
  const { handleError } = useQueryError();

  const { data: cards = [], isLoading, error } = useQuery({
    queryKey: queryKeys.cards.launched(selectedCategoryId),
    queryFn: () => fetchLaunchedCards(selectedCategoryId),
    onError: handleError,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // Procesar cards y organizar en grupos
  const cardGroups = React.useMemo(() => {
    const groups = [];
    for (let i = 0; i < cards.length; i += CARDS_PER_GROUP) {
      groups.push(cards.slice(i, Math.min(i + CARDS_PER_GROUP, cards.length)));
    }
    return groups;
  }, [cards]);

  // Obtener ancho del contenedor según tamaño
  const containerWidth = React.useMemo(() => {
    switch (componentSize) {
      case "Phone":
        return 362.4;
      case "Tablet":
        return 626;
      case "Desktop 1200":
        return 1057;
      case "Desktop 1440":
      default:
        return 1210;
    }
  }, [componentSize]);

  return {
    cardGroups,
    containerWidth,
    isLoading,
    error,
    isEmpty: cards.length === 0
  };
}